import { motion } from "framer-motion";
import { Reveal, EASE } from "@/lib/motion";
import { PageHero, PrimaryCTAs, SectionHeader, FaqBlock } from "@/components/site/PageParts";
import { CTABand } from "@/components/site/Bands";
import { SEO, breadcrumbSchema, faqSchema, serviceSchema } from "@/components/site/SEO";
import { RelatedWork } from "@/components/site/RelatedWork";

const CRUMBS = [
  { name: "Home", path: "/" },
  { name: "Services", path: "/services" },
  { name: "Content Strategy", path: "/services/content-strategy" },
];

const RELATED = [
  { tag: "Case Study", title: "Airmeet — 250+ pieces in 2 years", desc: "Multi-pillar architecture and roadmap in action.", to: "/case-studies" },
  { tag: "Process", title: "The Dandelion Method", desc: "How the strategy flows into the 6-step production system.", to: "/how-we-work" },
  { tag: "Service", title: "SEO/AEO/GEO Blog Content", desc: "The execution engine that turns the strategy into ranking articles.", to: "/services/blog-content" },
];

const PROBLEMS = [
  "Content published from internal brainstorms instead of buyer research.",
  "Targeting keywords with no realistic shot at ranking.",
  "Isolated articles with no internal linking or cluster architecture.",
  "No clear metrics for success — publishing on hope, not a plan.",
];

const COMPONENTS = [
  { n: "01", title: "Buyer Journey Mapping", desc: "6–12 buyer / prospect interviews, pain point analysis, search intent mapping, content gap analysis." },
  { n: "02", title: "Keyword Research & Topic Clustering", desc: "15–20 topic clusters, 100+ keywords ranked by opportunity, SERP analysis, AI search optimization opportunities." },
  { n: "03", title: "Pillar-Cluster Content Architecture", desc: "2–3 pillar pages, 20–30 supporting cluster articles, an internal linking strategy that actually compounds." },
  { n: "04", title: "90-Day Content Roadmap", desc: "Monthly calendar, priority sequencing, resource allocation, timeline expectations, success metrics." },
  { n: "05", title: "Competitive Intelligence", desc: "Content audit of 3–5 competitors, gap and opportunity analysis — where you can win, and how fast." },
];

const WEEKS = [
  { w: "Week 1", title: "Discovery & Research", body: "Kickoff, product demo review, buyer interviews, competitor audit, initial keyword research." },
  { w: "Week 2", title: "Strategy Development", body: "Journey mapping, clustering, architecture, mid-project check-in." },
  { w: "Week 3", title: "Roadmap & Delivery", body: "90-day roadmap, metrics framework, final strategy presentation." },
];

const DELIVERABLES = [
  "30–50 page strategy document",
  "Keyword spreadsheet (100+ keywords by cluster)",
  "90-day content calendar",
  "Content brief templates",
  "Success metrics dashboard guidance",
  "Recording of the strategy presentation",
];

const FAQS = [
  { q: "Do I need this if I already have a content calendar?", a: "Only if your \"strategy\" is more than a list of blog topics. If you already have buyer journey mapping, clustering, and competitive analysis in place, you're ahead of most companies." },
  { q: "Can you execute the strategy for us?", a: "Yes — most clients move to a monthly retainer after strategy is complete. We can also train your in-house team to run it." },
  { q: "How is this different from a content audit?", a: "An audit analyzes what you have. This builds what you need. Existing content gets audited as part of the process." },
];

export default function ContentStrategy() {
  return (
    <div data-testid="service-strategy-page">
      <SEO
        title="Strategic Content Planning for B2B SaaS"
        description="Build a content strategy that actually works. Buyer journey mapping, keyword clustering, pillar-cluster architecture, and a 90-day roadmap in 2–3 weeks."
        path="/services/content-strategy"
        jsonLd={[breadcrumbSchema(CRUMBS), faqSchema(FAQS), serviceSchema({ name: "Strategic Content Planning", description: "90-day content roadmap for B2B SaaS with buyer research, clustering, and architecture.", path: "/services/content-strategy" })]}
      />
      <PageHero
        eyebrow="Services · Content Strategy"
        lines={["Build a Content", "Strategy That"]}
        italicLast="Actually Works."
        sub="Most B2B SaaS companies jump straight to writing without a plan — and waste months creating content that doesn't rank or convert. We start with strategy. You get a 90-day roadmap with clear priorities, realistic timelines, and measurable goals before we write a single word."
        crumbs={CRUMBS}
      >
        <PrimaryCTAs />
      </PageHero>

      {/* Problem */}
      <section className="section-pad bg-[#F5F5F5]">
        <div className="max-w-[1400px] mx-auto px-6 md:px-10 grid grid-cols-1 lg:grid-cols-12 gap-12">
          <div className="lg:col-span-5">
            <SectionHeader eyebrow="The problem we solve" title="Publishing without a plan is expensive." />
          </div>
          <div className="lg:col-span-7">
            <ul className="border-t border-[#E0E0E0]">
              {PROBLEMS.map((p, i) => (
                <motion.li
                  key={i}
                  initial={{ opacity: 0, y: 12 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true, margin: "-40px" }}
                  transition={{ duration: 0.5, delay: i * 0.05, ease: EASE }}
                  className="py-5 border-b border-[#E0E0E0] flex items-start gap-4 text-[#111111]"
                >
                  <span className="font-mono text-xs text-[#999999] mt-2">0{i + 1}</span>
                  <span className="text-lg leading-relaxed">{p}</span>
                </motion.li>
              ))}
            </ul>
          </div>
        </div>
      </section>

      {/* What you get */}
      <section className="section-pad bg-white">
        <div className="max-w-[1400px] mx-auto px-6 md:px-10">
          <SectionHeader eyebrow="What you get" title="Five components. One coherent plan." />
          <div className="mt-14 grid grid-cols-1 md:grid-cols-2 border-t border-l border-[#E0E0E0]">
            {COMPONENTS.map((c, i) => (
              <Reveal key={c.n} delay={i * 0.06}>
                <div className="p-8 md:p-10 border-r border-b border-[#E0E0E0] h-full">
                  <span className="font-mono text-xs text-[#999999]">{c.n}</span>
                  <h3 className="mt-5 font-serif text-2xl md:text-[1.7rem] text-[#111111]">{c.title}</h3>
                  <p className="mt-4 text-[#666666] leading-relaxed">{c.desc}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* Weekly process */}
      <section className="section-pad bg-[#111111] text-white">
        <div className="max-w-[1400px] mx-auto px-6 md:px-10">
          <Reveal>
            <p className="text-xs uppercase tracking-[0.25em] text-[#666666] mb-6 font-mono">Process · 3 weeks</p>
            <h2 className="font-serif text-4xl md:text-5xl lg:text-6xl leading-[1.05] max-w-3xl">Kickoff to delivered strategy in three weeks.</h2>
          </Reveal>
          <div className="mt-16 grid grid-cols-1 md:grid-cols-3 border-t border-[#333333]">
            {WEEKS.map((w, i) => (
              <Reveal key={w.w} delay={i * 0.08}>
                <div className={`py-10 ${i !== 0 ? "md:pl-10" : ""} md:pr-10 ${i !== WEEKS.length - 1 ? "md:border-r border-[#333333] border-b md:border-b-0" : ""}`}>
                  <p className="font-mono text-xs uppercase tracking-[0.2em] text-[#666666]">{w.w}</p>
                  <h3 className="mt-4 font-serif text-3xl text-white">{w.title}</h3>
                  <p className="mt-5 text-[#E0E0E0] leading-relaxed">{w.body}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* Deliverables + Investment */}
      <section className="section-pad bg-white">
        <div className="max-w-[1400px] mx-auto px-6 md:px-10 grid grid-cols-1 lg:grid-cols-12 gap-12">
          <div className="lg:col-span-6">
            <SectionHeader eyebrow="What you walk away with" title="Every artifact. All yours to keep." />
            <ul className="mt-8 divide-y divide-[#E0E0E0] border-y border-[#E0E0E0]">
              {DELIVERABLES.map((d) => (
                <li key={d} className="py-4 flex items-start gap-3 text-[#111111]">
                  <span className="text-[#999999] font-mono text-sm mt-1">→</span>
                  <span className="text-lg">{d}</span>
                </li>
              ))}
            </ul>
          </div>
          <div className="lg:col-span-5 lg:col-start-8">
            <div className="border border-[#111111] p-8 md:p-10">
              <p className="text-xs uppercase tracking-[0.2em] text-[#999999]">Investment</p>
              <p className="mt-3 font-serif text-4xl md:text-5xl text-[#111111]">$4,500–$6,500</p>
              <p className="mt-4 text-[#666666]">Depending on complexity.</p>
              <div className="mt-8 pt-6 border-t border-[#E0E0E0]">
                <p className="text-xs uppercase tracking-[0.2em] text-[#999999]">Timeline</p>
                <p className="mt-3 font-serif text-3xl text-[#111111]">2–3 weeks</p>
                <p className="mt-3 text-[#666666]">From kickoff to delivery.</p>
              </div>
              <div className="mt-8 pt-6 border-t border-[#E0E0E0]">
                <p className="text-xs uppercase tracking-[0.2em] text-[#999999]">Ideal for</p>
                <p className="mt-3 text-[#111111]">Companies launching content marketing or rebuilding a failed strategy.</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="section-pad bg-[#F5F5F5]">
        <div className="max-w-[1400px] mx-auto px-6 md:px-10">
          <SectionHeader eyebrow="FAQ" title="Common questions." />
          <div className="mt-12">
            <FaqBlock items={FAQS} />
          </div>
        </div>
      </section>

      <CTABand heading="Get the strategy before you write another word." />
      <RelatedWork items={RELATED} />
    </div>
  );
}
